/**
 * /subcontracts — Subcontract management.
 *
 * Cross-project view of all subcontracts with status, amounts, and
 * insurance/workers-comp received flags. Construction-gated.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Box, Chip, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography, Tooltip,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { api } from "@/lib/api";
import { queryOptions } from "@tanstack/react-query";
import { useConstructionPack } from "@/lib/useConstructionPack";

export const Route = createFileRoute("/_auth/subcontracts")({
  component: SubcontractsPage,
});

interface Subcontract {
  id: string;
  subcontract_no: string;
  project_id: string;
  vendor_id: string;
  description: string | null;
  status: string;
  original_amount: string;
  approved_co_amount: string;
  retention_percent: string;
  executed_date: string | null;
  completed_date: string | null;
  insurance_received: boolean;
  workers_comp_received: boolean;
}

const subcontractsQueryOptions = () =>
  queryOptions({
    queryKey: ["subcontracts-all"],
    queryFn: () => api<Subcontract[]>("/api/projects/subcontracts/all"),
  });

const STATUS_COLOR: Record<string, "default" | "info" | "success" | "warning" | "error"> = {
  draft: "default",
  executed: "info",
  active: "success",
  complete: "success",
  terminated: "error",
};

function fmtMoney(v: string | number) {
  const n = typeof v === "string" ? parseFloat(v) : v;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

function Flag({ value, label }: { value: boolean; label: string }) {
  return (
    <Tooltip title={label}>
      {value
        ? <CheckIcon fontSize="small" color="success" />
        : <CloseIcon fontSize="small" color="disabled" />}
    </Tooltip>
  );
}

function SubcontractsPage() {
  const { enabled: constructionEnabled } = useConstructionPack();
  if (!constructionEnabled) return null;

  const q = useQuery(subcontractsQueryOptions());
  const subcontracts = q.data ?? [];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <AssignmentIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>Subcontracts</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          All subcontracts across projects
        </Typography>
      </Box>

      {q.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" } }}>
                <TableCell>Subcontract #</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Original Amount</TableCell>
                <TableCell align="right">CO Amount</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="center">Ret %</TableCell>
                <TableCell align="center">Ins</TableCell>
                <TableCell align="center">WC</TableCell>
                <TableCell>Executed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subcontracts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    No subcontracts found. Create them from the vendor detail page or via AI chat.
                  </TableCell>
                </TableRow>
              )}
              {subcontracts.map((sc) => {
                const total = parseFloat(sc.original_amount) + parseFloat(sc.approved_co_amount);
                return (
                  <TableRow key={sc.id} hover>
                    <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>{sc.subcontract_no}</TableCell>
                    <TableCell sx={{ color: "text.secondary", maxWidth: 200 }}>{sc.description ?? "—"}</TableCell>
                    <TableCell>
                      <Chip
                        label={sc.status}
                        size="small"
                        color={STATUS_COLOR[sc.status] ?? "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                      {fmtMoney(sc.original_amount)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                      {fmtMoney(sc.approved_co_amount)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                      {fmtMoney(total)}
                    </TableCell>
                    <TableCell align="center" sx={{ color: "text.secondary" }}>
                      {parseFloat(sc.retention_percent).toFixed(1)}%
                    </TableCell>
                    <TableCell align="center">
                      <Flag value={sc.insurance_received} label="Insurance certificate received" />
                    </TableCell>
                    <TableCell align="center">
                      <Flag value={sc.workers_comp_received} label="Workers comp certificate received" />
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>{sc.executed_date ?? "—"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
